import React, { useState, useEffect, useCallback } from 'react';
import { Send, Users, MessageSquare, Clock, Filter, Search, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';
import apiService from '../apiService';
import Img from '../components/Img';

// ─── Helpers ────────────────────────────────────────────────────────────────
const TARGETS = [
  { value: 'all',        label: '🌐 Tous les utilisateurs' },
  { value: 'clients',    label: '👥 Tous les clients' },
  { value: 'drivers',    label: '🚗 Tous les chauffeurs' },
  { value: 'deliverers', label: '🛵 Tous les livreurs' },
  { value: 'suppliers',  label: '🏪 Tous les fournisseurs' },
];

const formatDate = (d) =>
  d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '–';

// ─── Component ────────────────────────────────────────────────────────────────
export default function MessagesPage() {
  // Form state
  const [title, setTitle]           = useState('');
  const [message, setMessage]       = useState('');
  const [target, setTarget]         = useState('all');
  const [citySlug, setCitySlug]     = useState('');
  const [debtorsOnly, setDebtorsOnly] = useState(false);

  // Single-user mode
  const [singleMode, setSingleMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null); // { id, name, type }

  // Send state
  const [sending, setSending]       = useState(false);
  const [sentResult, setSentResult] = useState(null); // { count }
  const [error, setError]           = useState('');

  // Recipients count preview
  const [recipientCount, setRecipientCount] = useState(null);
  const [countLoading, setCountLoading]     = useState(false);

  // History
  const [history, setHistory]       = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Cities
  const [cities, setCities]         = useState([]);

  // Image (annonce)
  const [imageUrl, setImageUrl]       = useState(null);
  const [imageGcsPath, setImageGcsPath] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);

  // Lien d'action (CTA) : none | supplier | category | url
  const [ctaKind, setCtaKind]         = useState('none');
  const [ctaTargetId, setCtaTargetId] = useState('');
  const [ctaLabel, setCtaLabel]       = useState('');
  const [ctaSupplierQuery, setCtaSupplierQuery] = useState('');
  const [suppliers, setSuppliers]     = useState([]);
  const [categories, setCategories]   = useState([]);

  // ── Load cities ───────────────────────────────────────────────────────────
  useEffect(() => {
    apiService.get('/admin/my-cities', { userType: 'admin' }) // villes du périmètre de l'admin
      .then(res => { if (res.success) setCities(res.cities || []); })
      .catch(() => {});
  }, []);

  // ── Load suppliers + categories (pour le sélecteur de destination) ─────────
  useEffect(() => {
    apiService.get('/admin/delivery/suppliers', { userType: 'admin' })
      .then(res => { if (res.success) setSuppliers(res.data || res.suppliers || []); })
      .catch(() => {});
    apiService.get('/admin/delivery/categories', { userType: 'admin' })
      .then(res => { if (res.success) setCategories(res.data || res.categories || []); })
      .catch(() => {});
  }, []);

  // ── Upload image ───────────────────────────────────────────────────────────
  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permet de re-choisir le même fichier
    if (!file) return;
    setImageUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiService.upload('/admin/messages/upload-image', fd, { userType: 'admin' });
      if (res.success) { setImageUrl(res.url); setImageGcsPath(res.gcs_path); }
      else setError(res.error || 'Échec de l\'upload de l\'image');
    } catch (err) {
      setError(err?.message || 'Échec de l\'upload de l\'image');
    } finally {
      setImageUploading(false);
    }
  };

  const clearImage = () => { setImageUrl(null); setImageGcsPath(null); };

  const filteredSuppliers = ctaSupplierQuery.length >= 1
    ? suppliers.filter(s => (s.business_name || '').toLowerCase().includes(ctaSupplierQuery.toLowerCase())).slice(0, 8)
    : [];

  // ── Recipient count preview (debounced) ───────────────────────────────────
  const refreshCount = useCallback(async () => {
    if (singleMode) { setRecipientCount(selectedUser ? 1 : 0); return; }
    setCountLoading(true);
    try {
      const params = new URLSearchParams({ target });
      if (citySlug) params.set('city_slug', citySlug);
      if (debtorsOnly) params.set('debtors_only', 'true');
      const res = await apiService.get(`/admin/messages/recipients-count?${params}`, { userType: 'admin' });
      if (res.success) setRecipientCount(res.count);
    } catch {
      setRecipientCount(null);
    } finally {
      setCountLoading(false);
    }
  }, [singleMode, selectedUser, target, citySlug, debtorsOnly]);

  useEffect(() => {
    const timer = setTimeout(refreshCount, 400);
    return () => clearTimeout(timer);
  }, [refreshCount]);

  // ── Search users ──────────────────────────────────────────────────────────
  const searchUsers = useCallback(async (q) => {
    if (!q || q.length < 2) { setSearchResults([]); return; }
    setSearchLoading(true);
    try {
      let results = [];

      // Search clients
      const clientRes = await apiService.get(`/admin/users?q=${encodeURIComponent(q)}`, { userType: 'admin' });
      if (clientRes.success) {
        const ql = q.toLowerCase();
        const matched = (clientRes.users || []).filter(u =>
          u.name?.toLowerCase().includes(ql) ||
          u.phone?.includes(q) ||
          u.email?.toLowerCase().includes(ql)
        ).slice(0, 5);
        results = results.concat(matched.map(u => ({
          id: u.user_id,
          name: u.name,
          phone: u.phone,
          type: 'client',
          label: `👤 ${u.name} (${u.phone}) — Client`,
        })));
      }

      // Search drivers
      const driverRes = await apiService.get(`/admin/drivers?q=${encodeURIComponent(q)}`, { userType: 'admin' });
      if (driverRes.success) {
        const matched = (driverRes.drivers || []).filter(d =>
          d.name?.toLowerCase().includes(q.toLowerCase()) ||
          d.phone?.includes(q)
        ).slice(0, 5);
        results = results.concat(matched.map(d => ({
          id: d.driver_id,
          name: d.name,
          phone: d.phone,
          type: d.approved_roles?.includes('deliverer') ? 'deliverer' : 'driver',
          label: `🚗 ${d.name} (${d.phone}) — ${d.approved_roles?.includes('deliverer') ? 'Livreur' : 'Chauffeur'}`,
        })));
      }

      setSearchResults(results.slice(0, 10));
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchUsers(searchQuery), 400);
    return () => clearTimeout(t);
  }, [searchQuery, searchUsers]);

  // ── Send message ──────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!message.trim()) { setError('Le message ne peut pas être vide.'); return; }
    if (singleMode && !selectedUser) { setError('Sélectionnez un destinataire.'); return; }

    setError('');
    setSentResult(null);
    setSending(true);

    try {
      const filter = singleMode
        ? { single_user_id: selectedUser.id, single_user_type: selectedUser.type }
        : {
            target,
            ...(citySlug ? { city_slug: citySlug } : {}),
            ...(debtorsOnly ? { debtors_only: true } : {}),
          };

      const cta = ctaKind !== 'none' && ctaTargetId
        ? { kind: ctaKind, target_id: ctaTargetId.trim(), label: ctaLabel.trim() || undefined }
        : { kind: 'none' };

      const res = await apiService.post(
        '/admin/messages/broadcast',
        {
          title: title.trim() || undefined,
          message: message.trim(),
          filter,
          image_url: imageUrl || undefined,
          image_gcs_path: imageGcsPath || undefined,
          cta,
        },
        { userType: 'admin' }
      );

      if (res.success) {
        setSentResult({ count: res.sent_count });
        setTitle('');
        setMessage('');
        setSelectedUser(null);
        setSearchQuery('');
        clearImage();
        setCtaKind('none'); setCtaTargetId(''); setCtaLabel(''); setCtaSupplierQuery('');
        loadHistory();
      } else {
        setError(res.error || 'Erreur lors de l\'envoi');
      }
    } catch (e) {
      setError(e?.error || e?.message || 'Erreur de connexion');
    } finally {
      setSending(false);
    }
  };

  // ── History ───────────────────────────────────────────────────────────────
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await apiService.get('/admin/messages/history', { userType: 'admin' });
      if (res.success) setHistory(res.history || []);
    } catch {
      // silent
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // ── UI ────────────────────────────────────────────────────────────────────
  return (
    <div className="dashboard-content">
      <div className="page-header" style={{ marginBottom: '1.5rem' }}>
        <h1 className="page-title">💬 Messagerie Admin</h1>
        <p style={{ color: '#5C636E', margin: 0, fontSize: '0.95rem' }}>
          Envoyez des messages push &amp; in-app à vos utilisateurs
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: '1.5rem', alignItems: 'start' }}>

        {/* ── COMPOSE PANEL ── */}
        <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #E9EBEF', overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ background: 'linear-gradient(135deg, #C9350F 0%, #FF7800 100%)', padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <MessageSquare size={22} color="#fff" />
            <h2 style={{ margin: 0, color: '#fff', fontSize: '1.1rem', fontWeight: '700' }}>Nouveau message</h2>
          </div>

          <div style={{ padding: '1.5rem' }}>

            {/* Mode toggle */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', background: '#F4F5F7', borderRadius: '10px', padding: '4px' }}>
              {[
                { key: false, label: '📡 Broadcast', icon: Users },
                { key: true,  label: '👤 Ciblé', icon: Search },
              ].map(({ key, label }) => (
                <button
                  key={String(key)}
                  onClick={() => { setSingleMode(key); setSentResult(null); setError(''); }}
                  style={{
                    flex: 1, padding: '0.6rem', border: 'none', borderRadius: '8px', cursor: 'pointer',
                    fontWeight: '600', fontSize: '0.9rem', transition: 'all 0.2s',
                    background: singleMode === key ? '#C9350F' : 'transparent',
                    color: singleMode === key ? '#fff' : '#5C636E',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* ── Broadcast filters ── */}
            {!singleMode && (
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={labelStyle}>
                  <Filter size={14} style={{ marginRight: '4px' }} /> Destinataires
                </label>
                <select
                  value={target}
                  onChange={e => { setTarget(e.target.value); setDebtorsOnly(false); }}
                  style={selectStyle}
                >
                  {TARGETS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>

                {/* Ville */}
                <label style={{ ...labelStyle, marginTop: '0.75rem' }}>🏙️ Ville (optionnel)</label>
                <select
                  value={citySlug}
                  onChange={e => setCitySlug(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">Toutes les villes</option>
                  {cities.map(c => <option key={c.city_slug || c.slug} value={c.city_slug || c.slug}>{c.name}</option>)}
                </select>

                {/* Débiteurs */}
                {(target === 'clients' || target === 'drivers' || target === 'all') && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem', cursor: 'pointer', fontSize: '0.9rem', color: '#414852' }}>
                    <input
                      type="checkbox"
                      checked={debtorsOnly}
                      onChange={e => setDebtorsOnly(e.target.checked)}
                      style={{ width: '16px', height: '16px', accentColor: '#dc2626' }}
                    />
                    Solde débiteur uniquement (balance &lt; 0)
                  </label>
                )}

                {/* Preview count */}
                <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.9rem', background: '#FEE9E2', borderRadius: '8px', fontSize: '0.85rem', color: '#C9350F', fontWeight: '600' }}>
                  {countLoading
                    ? '⏳ Calcul...'
                    : recipientCount !== null
                      ? `📨 ${recipientCount.toLocaleString()} destinataires estimés`
                      : '–'}
                </div>
              </div>
            )}

            {/* ── Single user search ── */}
            {singleMode && (
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={labelStyle}><Search size={14} style={{ marginRight: '4px' }} /> Rechercher un utilisateur</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    placeholder="Nom, téléphone..."
                    value={searchQuery}
                    onChange={e => { setSearchQuery(e.target.value); setSelectedUser(null); }}
                    style={{ ...inputStyle, paddingLeft: '2.25rem' }}
                  />
                  <Search size={15} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9AA0A8' }} />
                </div>

                {searchLoading && (
                  <div style={{ padding: '0.5rem', color: '#5C636E', fontSize: '0.85rem' }}>Recherche…</div>
                )}

                {searchResults.length > 0 && !selectedUser && (
                  <div style={{ border: '1px solid #E9EBEF', borderRadius: '8px', marginTop: '4px', maxHeight: '180px', overflowY: 'auto', background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                    {searchResults.map(u => (
                      <div
                        key={u.id}
                        onClick={() => { setSelectedUser(u); setSearchQuery(u.label); setSearchResults([]); }}
                        style={{ padding: '0.6rem 0.9rem', cursor: 'pointer', fontSize: '0.875rem', color: '#414852', borderBottom: '1px solid #F4F5F7', transition: 'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#FEE9E2'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        {u.label}
                      </div>
                    ))}
                  </div>
                )}

                {selectedUser && (
                  <div style={{ marginTop: '0.5rem', padding: '0.6rem 0.9rem', background: '#ecfdf5', borderRadius: '8px', fontSize: '0.875rem', color: '#065f46', fontWeight: '600', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>✅ {selectedUser.name} ({selectedUser.type})</span>
                    <button onClick={() => { setSelectedUser(null); setSearchQuery(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontWeight: '700', fontSize: '1rem' }}>✕</button>
                  </div>
                )}
              </div>
            )}

            {/* Title */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Titre (optionnel)</label>
              <input
                type="text"
                placeholder="📩 Message de l'administration"
                value={title}
                onChange={e => setTitle(e.target.value)}
                style={inputStyle}
                maxLength={100}
              />
            </div>

            {/* Message */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>
                Message <span style={{ color: '#dc2626' }}>*</span>
                <span style={{ float: 'right', fontWeight: '400', color: '#9AA0A8' }}>{message.length}/500</span>
              </label>
              <textarea
                placeholder="Rédigez votre message ici..."
                value={message}
                onChange={e => setMessage(e.target.value)}
                maxLength={500}
                rows={5}
                style={{ ...inputStyle, resize: 'vertical', minHeight: '100px' }}
              />
            </div>

            {/* Image (optionnelle) */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>🖼️ Image (optionnelle)</label>
              {imageUrl ? (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <Img src={imageUrl} alt="aperçu" style={{ maxWidth: '100%', maxHeight: '160px', borderRadius: '10px', border: '1px solid #E9EBEF', display: 'block' }} />
                  <button
                    onClick={clearImage}
                    style={{ position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '999px', width: '26px', height: '26px', cursor: 'pointer', fontWeight: '700' }}
                    title="Retirer l'image"
                  >✕</button>
                </div>
              ) : (
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 0.9rem', border: '1px dashed #FFB900', borderRadius: '8px', background: '#FEE9E2', color: '#C9350F', fontSize: '0.85rem', fontWeight: '600', cursor: imageUploading ? 'wait' : 'pointer' }}>
                  {imageUploading ? '⏳ Téléversement…' : '📎 Choisir une image'}
                  <input type="file" accept="image/*" onChange={handleImagePick} disabled={imageUploading} style={{ display: 'none' }} />
                </label>
              )}
            </div>

            {/* Destination (lien d'action) */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>🎯 Action au clic (optionnel)</label>
              <select
                value={ctaKind}
                onChange={e => { setCtaKind(e.target.value); setCtaTargetId(''); setCtaSupplierQuery(''); }}
                style={selectStyle}
              >
                <option value="none">Aucune (message informatif)</option>
                <option value="supplier">Ouvrir un fournisseur</option>
                <option value="category">Ouvrir une catégorie</option>
                <option value="url">Ouvrir un lien (URL)</option>
              </select>

              {ctaKind === 'supplier' && (
                <div style={{ position: 'relative', marginTop: '0.6rem' }}>
                  <input
                    type="text"
                    placeholder="Rechercher un fournisseur…"
                    value={ctaSupplierQuery}
                    onChange={e => { setCtaSupplierQuery(e.target.value); setCtaTargetId(''); }}
                    style={inputStyle}
                  />
                  {filteredSuppliers.length > 0 && !ctaTargetId && (
                    <div style={{ border: '1px solid #E9EBEF', borderRadius: '8px', marginTop: '4px', maxHeight: '160px', overflowY: 'auto', background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                      {filteredSuppliers.map(s => (
                        <div
                          key={s.supplier_id}
                          onClick={() => { setCtaTargetId(s.supplier_id); setCtaSupplierQuery(s.business_name); }}
                          style={{ padding: '0.55rem 0.8rem', cursor: 'pointer', fontSize: '0.85rem', color: '#414852', borderBottom: '1px solid #F4F5F7' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#FEE9E2'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >🏪 {s.business_name}</div>
                      ))}
                    </div>
                  )}
                  {ctaTargetId && <div style={{ marginTop: '4px', fontSize: '0.8rem', color: '#065f46' }}>✅ Fournisseur sélectionné</div>}
                </div>
              )}

              {ctaKind === 'category' && (
                <select
                  value={ctaTargetId}
                  onChange={e => setCtaTargetId(e.target.value)}
                  style={{ ...selectStyle, marginTop: '0.6rem' }}
                >
                  <option value="">— Choisir une catégorie —</option>
                  {categories.map(c => (
                    <option key={c.category_id || c._id} value={c.category_id}>{c.name}</option>
                  ))}
                </select>
              )}

              {ctaKind === 'url' && (
                <input
                  type="url"
                  placeholder="https://…"
                  value={ctaTargetId}
                  onChange={e => setCtaTargetId(e.target.value)}
                  style={{ ...inputStyle, marginTop: '0.6rem' }}
                />
              )}

              {ctaKind !== 'none' && (
                <input
                  type="text"
                  placeholder="Texte du bouton (ex. « Commander maintenant »)"
                  value={ctaLabel}
                  onChange={e => setCtaLabel(e.target.value)}
                  maxLength={40}
                  style={{ ...inputStyle, marginTop: '0.6rem' }}
                />
              )}
            </div>

            {/* Error / Success */}
            {error && (
              <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '0.875rem' }}>
                ❌ {error}
              </div>
            )}
            {sentResult && (
              <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '8px', color: '#065f46', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <CheckCircle size={16} /> Message envoyé à <strong>{sentResult.count.toLocaleString()}</strong> destinataire{sentResult.count > 1 ? 's' : ''} !
              </div>
            )}

            {/* Send button */}
            <button
              onClick={handleSend}
              disabled={sending || !message.trim()}
              style={{
                width: '100%', padding: '0.85rem', border: 'none', borderRadius: '10px', cursor: sending ? 'not-allowed' : 'pointer',
                background: sending || !message.trim() ? '#DCE0E6' : 'linear-gradient(135deg, #C9350F 0%, #FF7800 100%)',
                color: '#fff', fontWeight: '700', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                transition: 'all 0.2s'
              }}
            >
              <Send size={18} />
              {sending ? 'Envoi en cours...' : 'Envoyer le message'}
            </button>
          </div>
        </div>

        {/* ── HISTORY PANEL ── */}
        <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #E9EBEF', overflow: 'hidden' }}>
          <button
            onClick={() => setShowHistory(v => !v)}
            style={{ width: '100%', padding: '1rem 1.25rem', background: '#FFFFFF', border: 'none', borderBottom: '1px solid #E9EBEF', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <span style={{ fontWeight: '700', color: '#414852', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Clock size={16} /> Historique des envois
            </span>
            {showHistory ? <ChevronUp size={18} color="#5C636E" /> : <ChevronDown size={18} color="#5C636E" />}
          </button>

          {showHistory && (
            <div style={{ maxHeight: '520px', overflowY: 'auto' }}>
              {historyLoading
                ? <div style={{ padding: '1.5rem', textAlign: 'center', color: '#5C636E', fontSize: '0.875rem' }}>Chargement…</div>
                : history.length === 0
                  ? <div style={{ padding: '1.5rem', textAlign: 'center', color: '#9AA0A8', fontSize: '0.875rem' }}>Aucun message envoyé</div>
                  : history.map((h, idx) => (
                    <div key={idx} style={{ padding: '0.9rem 1.1rem', borderBottom: '1px solid #F4F5F7' }}>
                      <div style={{ fontWeight: '700', color: '#C9350F', fontSize: '0.875rem', marginBottom: '2px' }}>
                        {h.title || '📩 Message de l\'administration'}
                      </div>
                      <div style={{ color: '#414852', fontSize: '0.85rem', marginBottom: '4px', lineHeight: '1.4' }}>
                        {h.message.length > 90 ? h.message.slice(0, 87) + '…' : h.message}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.775rem', color: '#9AA0A8' }}>
                        <span>👥 {h.sent_count} destinataires · {(h.targets || []).join(', ')}</span>
                        <span>{formatDate(h.sent_at)}</span>
                      </div>
                    </div>
                  ))
              }
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

// ─── Shared micro-styles ─────────────────────────────────────────────────────
const labelStyle = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: '600',
  color: '#5C636E',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: '0.4rem',
};

const selectStyle = {
  width: '100%',
  padding: '0.6rem 0.8rem',
  border: '1px solid #DCE0E6',
  borderRadius: '8px',
  fontSize: '0.9rem',
  color: '#414852',
  background: '#FFFFFF',
  cursor: 'pointer',
  outline: 'none',
};

const inputStyle = {
  width: '100%',
  padding: '0.65rem 0.85rem',
  border: '1px solid #DCE0E6',
  borderRadius: '8px',
  fontSize: '0.9rem',
  color: '#414852',
  background: '#FFFFFF',
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};
